/**
 * The rail and the drawer, 1:1 with `k3-shell.html`: the agency block, the role's menu, the bottom
 * block with the collapse button, and the Kapitan mark. Branch choice lives in the agency block,
 * because the design has no branch control in the top bar; screens read it from `useScopeStore`.
 */
import * as RadixDialog from "@radix-ui/react-dialog";
import { useQuery } from "@tanstack/react-query";
import { Check, ChevronsUpDown, PanelLeft } from "lucide-react";
import type { JSX } from "react";
import { Link, useLocation } from "react-router";

import { coreApi } from "@/api/endpoints";
import { queryKeys } from "@/api/queryKeys";
import { useTranslation, type TranslationKey } from "@/i18n";
import { cn } from "@/lib/cn";
import { useAuthStore } from "@/store/auth";
import { Logo } from "@/ui/brand";
import { DropdownMenu, type MenuItem } from "@/ui/dropdown-menu";
import { Tooltip } from "@/ui/tooltip";

import { bottomNav, isNavActive, mainNav, type NavItem } from "./navigation";
import { useScopeStore } from "./scope";
import { useShellPrefs } from "./shellPrefs";
import { useNavCounts } from "./useNavCounts";

const ACTIVE_RULE = { boxShadow: "inset 2px 0 0 var(--k-sidebar-rule)" };

function NavRow({
  item,
  collapsed,
  large,
  count,
  onNavigate,
}: {
  item: NavItem;
  collapsed: boolean;
  large: boolean;
  count: number | undefined;
  onNavigate?: (() => void) | undefined;
}): JSX.Element {
  const { t } = useTranslation();
  const { pathname } = useLocation();
  const active = isNavActive(item, pathname);
  const label = t(item.labelKey);
  const Icon = item.icon;
  const row = (
    <Link
      to={item.to}
      onClick={onNavigate}
      aria-current={active ? "page" : undefined}
      aria-label={collapsed ? label : undefined}
      style={active ? ACTIVE_RULE : undefined}
      className={cn(
        "flex shrink-0 items-center gap-2.5 rounded-control transition-colors duration-fast ease-token",
        large ? "h-11 text-base" : "h-8 text-sm",
        collapsed ? "justify-center" : "px-2.5",
        active ? "bg-sidebar-active font-semibold text-sidebar-ink-active" : "text-sidebar-ink hover:bg-sidebar-item-hover",
      )}
    >
      <Icon size={16} className={cn("shrink-0", active ? "text-sidebar-icon-active" : "text-sidebar-icon")} />
      {collapsed ? null : <span className="min-w-0 flex-1 truncate">{label}</span>}
      {collapsed || count === undefined || count === 0 ? null : (
        <span
          aria-label={t("nav.badgeCount", { count: String(count) })}
          className={cn(
            "inline-flex h-4.5 min-w-5 items-center justify-center rounded-pill px-1.5 text-xs font-normal tabular-nums",
            active ? "bg-sidebar-count-active text-sidebar-count-active-ink" : "bg-sidebar-count text-sidebar-count-ink",
          )}
        >
          {count}
        </span>
      )}
    </Link>
  );
  return <li className="flex flex-col">{collapsed ? <Tooltip content={label}>{row}</Tooltip> : row}</li>;
}

/** The agency block: who this is, and the agency and branch being looked at. */
function AgencyBlock({ collapsed }: { collapsed: boolean }): JSX.Element | null {
  const { t } = useTranslation();
  const user = useAuthStore((state) => state.user);
  const agencies = useAuthStore((state) => state.agencies);
  const switchAgency = useAuthStore((state) => state.switchAgency);
  const branchId = useScopeStore((state) => state.branchId);
  const setBranchId = useScopeStore((state) => state.setBranchId);
  // Only roles that span more than their own branch can choose one.
  const spansBranches = user?.role === "owner" || user?.role === "moliyachi";
  const branches = useQuery({
    queryKey: queryKeys.branches(),
    queryFn: () => coreApi.branches(),
    enabled: spansBranches,
    staleTime: 5 * 60_000,
  });
  if (user === null) return null;

  const name = user.tenant?.name ?? t("app.name");
  const chosen = branches.data?.find((branch) => branch.id === branchId);
  const subtitle = spansBranches ? (chosen?.name ?? t("common.allBranches")) : (user.branch?.name ?? t("common.allBranches"));

  const current = <Check strokeWidth={1.5} />;
  const items: readonly MenuItem[] = [
    { key: "agency-note", kind: "text", label: t("shell.agency.current") },
    ...(agencies.length > 1
      ? agencies.map((agency) => ({
          key: `agency-${String(agency.id)}`,
          label: `${agency.name} · ${t(`role.${agency.role}` as TranslationKey)}`,
          icon: agency.id === user.tenant?.id ? current : undefined,
          disabled: agency.id === user.tenant?.id,
          onSelect: (): void => {
            switchAgency(agency.id);
          },
        }))
      : [{ key: "agency-one", label: name, icon: current, disabled: true }]),
    ...(spansBranches && branches.data !== undefined
      ? [
          {
            key: "branch-all",
            label: t("common.allBranches"),
            icon: branchId === null ? current : undefined,
            separatorBefore: true,
            onSelect: (): void => {
              setBranchId(null);
            },
          },
          ...branches.data.map((branch) => ({
            key: `branch-${String(branch.id)}`,
            label: branch.name,
            icon: branch.id === branchId ? current : undefined,
            onSelect: (): void => {
              setBranchId(branch.id);
            },
          })),
        ]
      : []),
  ];

  return (
    <DropdownMenu
      label={collapsed ? name : `${name} ${subtitle}`}
      align="start"
      items={items}
      trigger={
        <button
          type="button"
          title={collapsed ? name : undefined}
          className={cn(
            "flex w-full items-center gap-2.5 rounded-control p-1.5 text-left transition-colors duration-fast ease-token hover:bg-sidebar-item-hover",
            collapsed && "justify-center",
          )}
        >
          <span aria-hidden="true" className="inline-flex size-7 shrink-0 items-center justify-center rounded-control bg-accent text-sm font-semibold text-btn-primary-ink">
            {name.slice(0, 1).toUpperCase()}
          </span>
          {collapsed ? null : (
            <>
              <span className="flex min-w-0 flex-1 flex-col leading-4">
                <b className="truncate text-sm font-semibold text-ink">{name}</b>
                <span className="truncate text-xs text-ink-subtle">{subtitle}</span>
              </span>
              <ChevronsUpDown className="shrink-0 text-ink-subtle" size={14} strokeWidth={1.5} aria-hidden="true" />
            </>
          )}
        </button>
      }
    />
  );
}

function Mark({ collapsed }: { collapsed: boolean }): JSX.Element {
  const { t } = useTranslation();
  const dark = useShellPrefs((state) => state.dark);
  return (
    <div
      title={t("app.name")}
      className={cn("flex items-center gap-1.5 border-t border-line px-3.5 pt-2.5 pb-3.5 text-xs font-medium text-ink-subtle", collapsed && "justify-center px-0")}
    >
      <Logo kind={dark ? "dark" : "mark"} size={14} />
      {collapsed ? null : <span>{t("app.name")}</span>}
    </div>
  );
}

/** Desktop rail (from 1024): 232px or 64px, light or the person's dark. */
export function Rail(): JSX.Element | null {
  const { t } = useTranslation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const collapsed = useShellPrefs((state) => state.collapsed);
  const setCollapsed = useShellPrefs((state) => state.setCollapsed);
  const dark = useShellPrefs((state) => state.dark);
  const counts = useNavCounts();
  if (role === undefined) return null;
  const toggleLabel = collapsed ? t("shell.expand") : t("shell.collapse");
  const row = (item: NavItem): JSX.Element => (
    <NavRow key={item.key} item={item} collapsed={collapsed} large={false} count={item.countKey === undefined ? undefined : counts[item.countKey]} />
  );
  return (
    <aside
      data-sidebar={dark ? "dark" : undefined}
      className="sticky top-0 hidden h-dvh shrink-0 flex-col overflow-x-hidden overflow-y-auto border-r border-line bg-sidebar transition-[width] duration-base ease-token lg:flex"
      style={{ width: collapsed ? "var(--k-sidebar-width-collapsed)" : "var(--k-sidebar-width)" }}
    >
      <div className="border-b border-line px-2 pt-3 pb-2.5">
        <AgencyBlock collapsed={collapsed} />
      </div>
      <nav aria-label={t("nav.aria.main")} className="flex-[1_0_auto] px-2 py-2.5">
        <ul className="flex flex-col gap-px">{mainNav(role).map(row)}</ul>
      </nav>
      <div className="flex flex-col gap-px p-2">
        <ul className="flex flex-col gap-px">{bottomNav(role).map(row)}</ul>
        <button
          type="button"
          title={toggleLabel}
          aria-label={toggleLabel}
          onClick={() => {
            setCollapsed(!collapsed);
          }}
          className={cn(
            "flex h-8 items-center gap-2.5 rounded-control text-sm text-sidebar-ink transition-colors duration-fast ease-token hover:bg-sidebar-item-hover",
            collapsed ? "justify-center" : "px-2.5",
          )}
        >
          <PanelLeft className="shrink-0 text-sidebar-icon" size={16} strokeWidth={1.5} aria-hidden="true" />
          {collapsed ? null : <span>{t("shell.collapseShort")}</span>}
        </button>
      </div>
      <Mark collapsed={collapsed} />
    </aside>
  );
}

/** Below 1024: the same menu in a 280px drawer. Always light, as the design has it. */
export function Drawer({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }): JSX.Element | null {
  const { t } = useTranslation();
  const role = useAuthStore((state) => state.user?.role ?? undefined);
  const counts = useNavCounts();
  if (role === undefined) return null;
  const close = (): void => {
    onOpenChange(false);
  };
  return (
    <RadixDialog.Root open={open} onOpenChange={onOpenChange}>
      <RadixDialog.Portal>
        <RadixDialog.Overlay className="fixed inset-0 z-50 bg-overlay" />
        <RadixDialog.Content
          aria-describedby={undefined}
          className="fixed inset-y-0 left-0 z-50 flex w-70 max-w-[86vw] flex-col overflow-y-auto overscroll-contain border-r border-line bg-sidebar [animation:k-slide_180ms_ease-out]"
        >
          <RadixDialog.Title className="sr-only">{t("shell.drawer")}</RadixDialog.Title>
          <div className="border-b border-line px-3 pt-3 pb-2.5">
            <AgencyBlock collapsed={false} />
          </div>
          <nav aria-label={t("nav.aria.main")} className="flex-[1_0_auto] px-2 py-2.5">
            <ul className="flex flex-col gap-px">
              {[...mainNav(role), ...bottomNav(role)].map((item) => (
                <NavRow key={item.key} item={item} collapsed={false} large count={item.countKey === undefined ? undefined : counts[item.countKey]} onNavigate={close} />
              ))}
            </ul>
          </nav>
          <Mark collapsed={false} />
        </RadixDialog.Content>
      </RadixDialog.Portal>
    </RadixDialog.Root>
  );
}
